/**
 * Copyright (c) Qwoach LLC 2025. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Created by Artem Bogush <artem@qwoach.com> on 06.06.25
 *
 */

import LineItem from "@shared/Invoice/LineItem";
import {CURRENCY_FORMATTING_OPTIONS_SHORT, currencyFormatted} from "@shared/Profile/CurrencyItem";
import {getSectionIdForServiceType, getServiceOptionsByType} from "@shared/Quote/ServiceType";
import {ServiceUsageType} from "@shared/Invoice/ServiceUsageType";
import {Type} from "@qwoach/class-transformer-wrapper";

export default class ServiceItem extends LineItem {

	@Type(() => ServiceItem)
	subLineItems: ServiceItem[] = []

	hasQuantityNumber: boolean = false;
	hasQuantity: boolean = true;
	hasPrice: boolean = false;
	hasDescription: boolean = false;

	sectionId: string = null;
	quantityLabel: string = null;
	quantityNumberLabel: string = null;
	priceLabel: string = null;

	quantityNumberUsed: number = 0
	quantityUsed: number = 0
	itemPriceCentsUsed: number = 0

	_hideDelete: boolean = false; //for UI only
	rateType: string = null
	isGlobalDefaultOverridden: boolean = false
	relatedServiceStates: Object = null

	fy1UsageValue: number = null


	constructor(options) {
		super(options);
		this.applyOptions(options);
		this.syncDerivedConfig();
	}

	normalizeNumericDefault(value, fallback = 1) {
		let normalized = Number(value)
		if (value === null || value === undefined || Number.isNaN(normalized)) {
			return fallback
		}
		return normalized
	}

	syncDerivedConfig() {
		let serviceOptions = this.serviceType ? getServiceOptionsByType(this.serviceType) || {} : {}
		if (!this.sectionId && this.serviceType) {
			this.sectionId = getSectionIdForServiceType(this.serviceType);
		}
		if (!this.quantityLabel && serviceOptions.quantityLabel) {
			this.quantityLabel = serviceOptions.quantityLabel;
		}
		if (!this.quantityNumberLabel && serviceOptions.quantityNumberLabel) {
			this.quantityNumberLabel = serviceOptions.quantityNumberLabel;
		}
		if (!this.priceLabel && serviceOptions.priceLabel) {
			this.priceLabel = serviceOptions.priceLabel;
		}
		switch (this.serviceUsageType) {
			case ServiceUsageType.PRICE:
				this.hasQuantityNumber = false;
				this.hasQuantity = false;
				this.hasPrice = true;
				this.priceLabel = this.priceLabel || "Total Amount";
				break
			case ServiceUsageType.QUANTITY_NUMBER:
				this.hasQuantity = true;
				this.hasPrice = false;
				this.quantityNumber = this.normalizeNumericDefault(this.quantityNumber, 1);
				this.quantity = this.normalizeNumericDefault(this.quantity, 1);
				this.quantityNumberUnits = this.quantityNumberUnits || "session";
				this.quantityUnits = this.quantityUnits || "hour";
				break
			case ServiceUsageType.QUANTITY:
				this.hasQuantityNumber = false;
				this.hasQuantity = true;
				this.hasPrice = false;
				this.quantityNumber = this.normalizeNumericDefault(this.quantityNumber, 1);
				this.quantity = this.normalizeNumericDefault(this.quantity, 1);
				this.quantityUnits = this.quantityUnits || "hour";
				break
			default:
				break
		}
		for (let subItem of this.subLineItems || []) {
			subItem.syncDerivedConfig?.();
		}
	}

	hasCountField() {
		return this.serviceUsageType === ServiceUsageType.QUANTITY_NUMBER;
	}

	hasAmountField() {
		return !this.isUsageByPrice();
	}

	resetPrimaryValue() {
		let serviceOptions = getServiceOptionsByType(this.serviceType) || {}
		switch (this.serviceUsageType) {
			case ServiceUsageType.PRICE:
				this.itemPriceCents = serviceOptions.itemPriceCents ?? null
				break
			case ServiceUsageType.QUANTITY:
			case ServiceUsageType.QUANTITY_NUMBER:
				this.quantity = serviceOptions.quantity ?? null
				break
			default:
				break
		}
		return this
	}

	usesRate() {
		return !this.isUsageByPrice();
	}

	isFixedFee() {
		return this.isUsageByPrice();
	}

	computeCommissionValues(commissionDetails) {
		if (!commissionDetails) {
			return;
		}

		this.quantityTotalLogged = commissionDetails.getLogValueTotalForServiceItem(this, (serviceLogItem, {isDraft}) => {
			if (!isDraft) {
				return serviceLogItem.usedQuantity
			}
		});
	}

	getFy1UsageValue() {
		let totalUsageValue = this.getUsageValue();
		let fy1UsageValue = this.fy1UsageValue;
		if (totalUsageValue <= 0) {
			return 0;
		}
		if (fy1UsageValue === null || fy1UsageValue === undefined || isNaN(fy1UsageValue)) {
			return totalUsageValue;
		}
		return Math.min(Math.max(Number(fy1UsageValue), 0), totalUsageValue);
	}

	getFy2UsageValue() {
		let totalUsageValue = this.getUsageValue();
		return Math.max(totalUsageValue - this.getFy1UsageValue(), 0);
	}

	setFy1UsageValue(value) {
		this.fy1UsageValue = Number(value);
	}

	setFy2UsageValue(value) {
		let totalUsageValue = this.getUsageValue();
		let fy2UsageValue = Number(value);
		if (isNaN(fy2UsageValue)) {
			return;
		}
		this.fy1UsageValue = totalUsageValue - fy2UsageValue;
	}

	getFy1AmountCents() {
		let totalCents = this.getTotalPriceCents();
		let totalCentsRounded = Math.round(totalCents);
		let totalUsageValue = this.getUsageValue();
		if (totalUsageValue <= 0) {
			return totalCentsRounded;
		}
		let ratio = this.getFy1UsageValue() / totalUsageValue;
		return Math.min(Math.max(Math.round(totalCentsRounded * ratio), 0), totalCentsRounded);
	}

	getFy2AmountCents() {
		let totalCentsRounded = Math.round(this.getTotalPriceCents());
		return Math.max(totalCentsRounded - this.getFy1AmountCents(), 0);
	}

	normalizeFySplit() {
		if (this.fy1UsageValue === null || this.fy1UsageValue === undefined || isNaN(this.fy1UsageValue)) {
			return;
		}
		let totalUsageValue = this.getUsageValue();
		if (totalUsageValue <= 0) {
			return;
		}
		let normalized = Math.min(Math.max(Number(this.fy1UsageValue), 0), totalUsageValue);
		if (normalized !== this.fy1UsageValue) {
			this.fy1UsageValue = normalized;
		}
	}

	getFySplitStep() {
		if (this.isUsageByPrice()) {
			return 100;
		}
		if (this.isUsageByQuantityNumber()) {
			return 1;
		}
		return 0.25;
	}

	getFy1AmountFormatted(currencyCode = this.currencyCode) {
		return currencyFormatted(currencyCode, this.getFy1AmountCents() / 100, CURRENCY_FORMATTING_OPTIONS_SHORT);
	}

	getFy2AmountFormatted(currencyCode = this.currencyCode) {
		return currencyFormatted(currencyCode, this.getFy2AmountCents() / 100, CURRENCY_FORMATTING_OPTIONS_SHORT);
	}

	migrateTitle() {
		let currentTitle = getServiceOptionsByType(this.serviceType)?.title
		if (currentTitle) {
			this.title = currentTitle
		}
	}


}
